// pages/contact.js

import emailjs from "@emailjs/browser";
import Head from "next/head";
import { useRef, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import FormInput from "../components/FormInput";

export default function Contact() {
  const formRef = useRef();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
    website: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const validate = () => {
    const errors = {};
    if (!form.name) errors.name = "Name is required.";
    if (!form.email) {
      errors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      errors.email = "Please enter a valid email address.";
    }
    if (!form.message) {
      errors.message = "Message is required.";
    } else if (form.message.length < 10) {
      errors.message = "Message must be at least 10 characters.";
    }
    return errors;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot check: If the hidden 'website' field is filled, it's a bot.
    if (form.website) {
      console.log("Bot detected. Silently ignoring.");
      setSubmitted(true); // Fake success
      return;
    }

    const errors = validate();
    setFieldErrors(errors);
    setErrorMessage("");

    if (Object.keys(errors).length === 0) {
      setIsSending(true);

      try {
        // reCAPTCHA is enforced in production only. In development the dev
        // server is reached via hosts not registered for the site key, where
        // executeRecaptcha throws "Invalid site key", so we skip it entirely.
        const isProd = process.env.NODE_ENV === "production";

        let token = "";
        if (isProd) {
          if (!executeRecaptcha) {
            const err = new Error("reCAPTCHA not ready");
            err.code = "recaptcha_unavailable";
            throw err;
          }
          console.log("Executing reCAPTCHA...");
          token = await executeRecaptcha("contact_form");
          console.log("reCAPTCHA token received:", token ? "Success" : "Failed");
        } else {
          console.warn("[dev] Skipping reCAPTCHA (non-production).");
        }

        // Verify reCAPTCHA token
        const verifyResponse = await fetch("/api/verify-recaptcha", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });

        const verifyData = await verifyResponse.json();
        console.log("reCAPTCHA verify:", verifyData);

        if (!verifyData.success || verifyData.score < 0.5) {
          const err = new Error("Security verification failed");
          err.code = "recaptcha_failed";
          throw err;
        }

        // Send email via EmailJS
        const result = await emailjs.sendForm(
          process.env.NEXT_PUBLIC_EMAILJS_SERVICE_ID,
          process.env.NEXT_PUBLIC_EMAILJS_TEMPLATE_ID,
          formRef.current,
          process.env.NEXT_PUBLIC_EMAILJS_PUBLIC_KEY,
        );

        console.log("EmailJS response:", result.status, result.text);
        setSubmitted(true);
      } catch (error) {
        console.error("Form submission error:", error);
        const messages = {
          recaptcha_unavailable:
            "Security check couldn't load. Please refresh the page and try again.",
          recaptcha_failed: "We couldn't verify you're human. Please try again.",
        };
        setErrorMessage(
          messages[error?.code] ||
            "Failed to send message. Please try again later.",
        );
      } finally {
        setIsSending(false);
      }
    } else {
      console.log("Form has errors:", errors);
    }
  };

  if (submitted) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          maxWidth: "32rem",
          margin: "3rem auto",
          padding: "2rem",
          textAlign: "center",
          border: "1px solid #b7e4c7",
          background: "#ebfaf0",
          borderRadius: "0.75rem",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: "3rem",
            height: "3rem",
            lineHeight: "3rem",
            margin: "0 auto 1rem",
            borderRadius: "50%",
            background: "#2e7d32",
            color: "#fff",
            fontSize: "1.5rem",
          }}
        >
          ✓
        </div>
        <h3 style={{ fontWeight: "bold", margin: "0 0 0.5rem" }}>
          Thank you! Your message has been sent.
        </h3>
        <p style={{ margin: 0, color: "#333" }}>
          We&apos;ve received your enquiry and will get back to you shortly.
        </p>
      </div>
    );
  }

  return (
    <>
      <Head>
        <title>Contact Us</title>
      </Head>
      <main>
        <section className="section-alt">
          <div className="form-container">
            <form ref={formRef} onSubmit={handleSubmit} noValidate>
              {/* Honeypot Field - Invisible to humans, tempting for bots */}
              <div
                style={{ position: "absolute", left: "-9999px" }}
                aria-hidden="true"
              >
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  tabIndex="-1"
                  autoComplete="off"
                  value={form.website}
                  onChange={handleChange}
                />
              </div>

              {Object.keys(fieldErrors).length > 0 && (
                <div
                  role="alert"
                  style={{ color: "red", marginBottom: "1rem" }}
                >
                  Please fix the errors below.
                </div>
              )}
              {errorMessage && (
                <div
                  role="alert"
                  aria-live="assertive"
                  style={{
                    marginBottom: "1rem",
                    padding: "0.75rem 1rem",
                    color: "#842029",
                    background: "#f8d7da",
                    border: "1px solid #f5c2c7",
                    borderRadius: "0.5rem",
                  }}
                >
                  {errorMessage}
                </div>
              )}

              <FormInput
                label="Name"
                id="name"
                name="name"
                type="text"
                value={form.name}
                onChange={handleChange}
                required
                error={fieldErrors.name}
              />
              <FormInput
                label="Email"
                id="email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                error={fieldErrors.email}
              />
              <div>
                <label htmlFor="message">Message*</label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  aria-invalid={!!fieldErrors.message}
                  className="form-textarea"
                />
                {fieldErrors.message && (
                  <span role="alert" style={{ color: "red" }}>
                    {fieldErrors.message}
                  </span>
                )}
              </div>

              <div
                style={{ margin: "1rem 0", fontSize: "0.85rem", color: "#666" }}
              >
                This site is protected by reCAPTCHA and the Google{" "}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="https://policies.google.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </a>{" "}
                apply.
              </div>

              <button type="submit" className="button" disabled={isSending}>
                {isSending ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </section>
      </main>
    </>
  );
}
